import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';
import { ZagDateTimePipe } from '../formatting/zag-date-time.pipe';
import { BulkEditJobService } from './bulk-edit-job.service';
import type { BulkJobSummary, BulkJobStatus } from './bulk-edit.models';

type StatusFilter = BulkJobStatus | null;

const STATUS_ICON: Record<BulkJobStatus, string> = {
  running: 'sync',
  finished: 'check-circle',
  failed: 'error',
  cancelled: 'cancel',
};

@Component({
  selector: 'zag-recent-bulk-edits-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ZagButton, ZagIcon, ZagDateTimePipe],
  template: `
    <div class="zag-recent-bulk-card">
      <button
        zagButton="basic"
        zagType="secondary"
        class="zag-recent-bulk-card__header"
        type="button"
        (click)="collapsed.update(v => !v)"
        [attr.aria-expanded]="!collapsed()"
        [attr.aria-controls]="'zrbc-list-' + collectionKey()"
      >
        <span zagIcon="history" class="zag-recent-bulk-card__header-icon" aria-hidden="true"></span>
        <span class="zag-recent-bulk-card__title">{{ 'bulkEdit.recentJobs' | translate }}</span>
        <span [zagIcon]="collapsed() ? 'expand-more' : 'expand-less'" class="zag-recent-bulk-card__chevron" aria-hidden="true"></span>
      </button>

      @if (!collapsed()) {
        <div [id]="'zrbc-list-' + collectionKey()">
          <div class="zag-recent-bulk-card__filter-bar">
            <button
              zagButton="outlined"
              zagType="secondary"
              zagSize="sm"
              class="zag-recent-bulk-card__filter-btn"
              [class.zag-recent-bulk-card__filter-btn--active]="statusFilter() === null"
              type="button"
              (click)="statusFilter.set(null)"
            >
              {{ 'bulkEdit.filterAll' | translate }}
            </button>
            <button
              zagButton="outlined"
              zagType="secondary"
              zagSize="sm"
              class="zag-recent-bulk-card__filter-btn"
              [class.zag-recent-bulk-card__filter-btn--active]="statusFilter() === 'running'"
              type="button"
              [attr.aria-label]="'bulkEdit.filterRunning' | translate"
              (click)="statusFilter.set('running')"
            >
              <span
                zagIcon="sync"
                class="zag-recent-bulk-card__filter-icon zag-recent-bulk-card__filter-icon--running"
                aria-hidden="true"
              ></span>
            </button>
            <button
              zagButton="outlined"
              zagType="secondary"
              zagSize="sm"
              class="zag-recent-bulk-card__filter-btn"
              [class.zag-recent-bulk-card__filter-btn--active]="statusFilter() === 'finished'"
              type="button"
              [attr.aria-label]="'bulkEdit.filterDone' | translate"
              (click)="statusFilter.set('finished')"
            >
              <span
                zagIcon="check-circle"
                class="zag-recent-bulk-card__filter-icon zag-recent-bulk-card__filter-icon--finished"
                aria-hidden="true"
              ></span>
            </button>
            <button
              zagButton="outlined"
              zagType="secondary"
              zagSize="sm"
              class="zag-recent-bulk-card__filter-btn"
              [class.zag-recent-bulk-card__filter-btn--active]="statusFilter() === 'failed'"
              type="button"
              [attr.aria-label]="'bulkEdit.filterFailed' | translate"
              (click)="statusFilter.set('failed')"
            >
              <span
                zagIcon="error"
                class="zag-recent-bulk-card__filter-icon zag-recent-bulk-card__filter-icon--failed"
                aria-hidden="true"
              ></span>
            </button>
          </div>

          @if (recentJobs.isLoading()) {
            <div class="zag-recent-bulk-card__loading">
              <span zagIcon="sync" class="zag-recent-bulk-card__spinner" aria-hidden="true"></span>
            </div>
          } @else if (recentJobs.error()) {
            <div class="zag-recent-bulk-card__error" role="alert">
              <span zagIcon="error-outline" aria-hidden="true"></span>
              <p>{{ 'common.error' | translate }}</p>
              <button zagButton="outlined" zagType="secondary" zagSize="sm" type="button" (click)="retry()">
                {{ 'common.retry' | translate }}
              </button>
            </div>
          } @else if (filteredJobs().length === 0) {
            <p class="zag-recent-bulk-card__empty">{{ 'bulkEdit.noRecentJobs' | translate }}</p>
          } @else {
            <ul class="zag-recent-bulk-card__list" role="list">
              @for (job of filteredJobs(); track job.id) {
                <li>
                  <button zagButton="basic" zagType="secondary" type="button" class="zag-recent-bulk-card__item" (click)="openJob(job.id)">
                    <span
                      [zagIcon]="getStatusIcon(job)"
                      class="zag-recent-bulk-card__status-icon"
                      [class.zag-recent-bulk-card__status-icon--running]="deriveStatus(job) === 'running'"
                      [class.zag-recent-bulk-card__status-icon--finished]="deriveStatus(job) === 'finished'"
                      [class.zag-recent-bulk-card__status-icon--failed]="deriveStatus(job) === 'failed'"
                      [class.zag-recent-bulk-card__status-icon--cancelled]="deriveStatus(job) === 'cancelled'"
                      aria-hidden="true"
                    ></span>
                    <div class="zag-recent-bulk-card__item-info">
                      <time class="zag-recent-bulk-card__item-date" [attr.datetime]="job.createdAt">{{ job.createdAt | zagDateTime }}</time>
                      <span class="zag-recent-bulk-card__item-totals">
                        @if (job.totals) {
                          {{ 'bulkEdit.applySuccess' | translate: job.totals }}
                        } @else if (deriveStatus(job) === 'running') {
                          {{ 'bulkEdit.jobRunning' | translate }}
                        }
                      </span>
                    </div>
                  </button>
                </li>
              }
            </ul>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .zag-recent-bulk-card {
        display: flex;
        flex-direction: column;
      }

      .zag-recent-bulk-card__header.zag-button {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
        min-height: 42px;
        font-family: var(--font-family-primary);
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: var(--text-color-secondary);
        padding: var(--spacing-sm) var(--spacing-md);
        background: var(--surface-color-secondary);
        border: none;
        border-bottom: 1px solid var(--border-color-default);
        border-radius: 0;
        cursor: pointer;
        text-align: left;
        transition: background-color var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-recent-bulk-card__header:hover {
        background: var(--surface-color-hover);
        color: var(--text-color-primary);
      }

      .zag-recent-bulk-card__header:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: -2px;
      }

      .zag-recent-bulk-card__header-icon,
      .zag-recent-bulk-card__chevron {
        font-size: var(--icon-size-secondary);
        flex-shrink: 0;
      }

      .zag-recent-bulk-card__title {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-recent-bulk-card__filter-bar {
        display: flex;
        gap: var(--spacing-xs);
        padding: var(--spacing-sm) var(--spacing-md);
        border-bottom: 1px solid var(--border-color-default);
        background: var(--surface-color-default);
      }

      .zag-recent-bulk-card__filter-btn.zag-button {
        flex: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 32px;
        padding: var(--spacing-xs) var(--spacing-sm);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-sm);
        background: transparent;
        font-family: var(--font-family-primary);
        font-size: var(--font-size-caption);
        color: var(--text-color-secondary);
        cursor: pointer;
        transition:
          background-color var(--transition-duration-fast) var(--transition-timing),
          border-color var(--transition-duration-fast) var(--transition-timing),
          color var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-recent-bulk-card__filter-btn:hover {
        background: var(--surface-color-hover);
        color: var(--text-color-primary);
      }

      .zag-recent-bulk-card__filter-btn:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: -2px;
      }

      .zag-recent-bulk-card__filter-btn--active {
        background: color-mix(in srgb, var(--color-primary) 15%, transparent);
        border-color: var(--color-primary);
        color: var(--text-color-primary);
      }

      .zag-recent-bulk-card__filter-icon {
        font-size: var(--icon-size-secondary);
        line-height: 1;
      }

      .zag-recent-bulk-card__filter-icon--running {
        color: var(--color-primary);
      }

      .zag-recent-bulk-card__filter-icon--finished {
        color: var(--color-success);
      }

      .zag-recent-bulk-card__filter-icon--failed {
        color: var(--color-error);
      }

      .zag-recent-bulk-card__loading {
        display: flex;
        justify-content: center;
        padding: var(--spacing-md);
      }

      .zag-recent-bulk-card__spinner {
        animation: zrbc-spin var(--animation-spin-duration, 1s) linear infinite;
      }

      @keyframes zrbc-spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      .zag-recent-bulk-card__empty {
        font-size: var(--font-size-sm);
        color: var(--text-color-muted);
        text-align: center;
        padding: var(--spacing-md);
        margin: 0;
      }

      .zag-recent-bulk-card__list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
      }

      .zag-recent-bulk-card__item.zag-button {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
        min-height: 42px;
        padding: var(--spacing-sm) var(--spacing-md);
        background: transparent;
        border: none;
        border-bottom: 1px solid var(--border-color-default);
        border-radius: 0;
        color: var(--text-color-secondary);
        cursor: pointer;
        font-family: var(--font-family-primary);
        text-align: left;
        transition:
          background-color var(--transition-duration-fast) var(--transition-timing),
          color var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-recent-bulk-card__list li:last-child .zag-recent-bulk-card__item {
        border-bottom: none;
      }

      .zag-recent-bulk-card__item:hover {
        background: var(--surface-color-hover);
        color: var(--text-color-primary);
      }

      .zag-recent-bulk-card__item:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: -2px;
      }

      .zag-recent-bulk-card__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-md);
        color: var(--color-error);
        text-align: center;
      }

      .zag-recent-bulk-card__error p {
        margin: 0;
      }

      .zag-recent-bulk-card__status-icon {
        font-size: var(--icon-size-secondary);
        color: var(--text-color-secondary);
        flex-shrink: 0;
      }

      .zag-recent-bulk-card__status-icon--running {
        color: var(--color-primary);
        animation: zrbc-spin var(--animation-spin-duration, 1s) linear infinite;
      }

      .zag-recent-bulk-card__status-icon--finished {
        color: var(--color-success-foreground);
      }

      .zag-recent-bulk-card__status-icon--failed {
        color: var(--color-error);
      }

      .zag-recent-bulk-card__status-icon--cancelled {
        color: var(--text-color-muted);
      }

      .zag-recent-bulk-card__item-info {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-2xs);
        flex: 1;
        min-width: 0;
      }

      .zag-recent-bulk-card__item-date {
        font-size: var(--font-size-xs);
        color: var(--text-color-muted);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .zag-recent-bulk-card__item-totals {
        font-size: var(--font-size-xs);
        color: var(--text-color-secondary);
      }
    `,
  ],
})
export class RecentBulkEditsCardComponent {
  readonly collectionKey = input.required<string>();

  private readonly jobService = inject(BulkEditJobService);
  private readonly router = inject(Router);

  readonly recentJobs = rxResource({
    params: () => ({ collectionKey: this.collectionKey() }),
    stream: ({ params }) => this.jobService.getRecentJobs(params.collectionKey),
  });

  protected readonly collapsed = signal(false);
  protected readonly statusFilter = signal<StatusFilter>(null);

  protected retry(): void {
    this.recentJobs.reload();
  }

  protected readonly filteredJobs = computed(() => {
    const jobs = this.recentJobs.value() ?? [];
    const filter = this.statusFilter();
    if (!filter) return jobs;
    return jobs.filter(j => this.deriveStatus(j) === filter);
  });

  protected deriveStatus(job: BulkJobSummary): BulkJobStatus {
    if (job.status) return job.status;
    if (!job.completedAt) return 'running';
    return (job.totals?.failed ?? 0) > 0 ? 'failed' : 'finished';
  }

  protected getStatusIcon(job: BulkJobSummary): string {
    return STATUS_ICON[this.deriveStatus(job)];
  }

  openJob(jobId: string): void {
    this.router.navigate([], { queryParams: { bulkJob: jobId }, queryParamsHandling: 'merge' });
  }
}
